// src/components/dashboard/ExcursionsChart.jsx
import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { fetchExcursoesPorPeriodo } from "services/dashboardService";
import { formatDateToYYYYMMDD } from "utils/dateUtils";

// Mantendo sua lógica de datas
const getDefaultDate = () => {
  const hoje = new Date();
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 3, 0);
  return { inicio, fim };
};

function ExcursionsChart() {
  const { inicio, fim } = getDefaultDate();
  const [startDate, setStartDate] = useState(inicio);
  const [endDate, setEndDate] = useState(fim);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadChartData = async () => {
      setIsLoading(true);
      const data = await fetchExcursoesPorPeriodo(startDate, endDate);
      setChartData(data);
      setIsLoading(false);
    };

    loadChartData();
  }, [startDate, endDate]);

  return (
    <Card className="col-span-1 lg:col-span-2">
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
          <div className="space-y-1">
            <CardTitle>Excursões Agendadas</CardTitle>
            <CardDescription>
              Total de excursões agendadas por mês.
            </CardDescription>
          </div>

          <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
            <div className="space-y-1">
              <Label
                htmlFor="start-date"
                className="text-xs text-muted-foreground"
              >
                De
              </Label>
              <Input
                id="start-date"
                type="date"
                value={formatDateToYYYYMMDD(startDate)}
                onChange={(e) =>
                  setStartDate(new Date(e.target.value + "T00:00:00"))
                }
                className="h-9 force-light-datepicker w-full"
              />
            </div>
            <div className="space-y-1">
              <Label
                htmlFor="end-date"
                className="text-xs text-muted-foreground"
              >
                Até
              </Label>
              <Input
                id="end-date"
                type="date"
                value={formatDateToYYYYMMDD(endDate)}
                onChange={(e) =>
                  setEndDate(new Date(e.target.value + "T00:00:00"))
                }
                className="h-9 force-light-datepicker w-full"
              />
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="h-[300px] w-full pt-4">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-muted-foreground animate-pulse">
              Carregando dados...
            </p>
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="4 4" vertical={false} />
              <XAxis
                dataKey="name"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickMargin={10}
              />
              <YAxis
                allowDecimals={false}
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                contentStyle={{
                  backgroundColor: "hsl(var(--background))",
                  borderColor: "hsl(var(--border))",
                  borderRadius: "0.5rem",
                  fontSize: "14px",
                }}
                labelStyle={{
                  color: "hsl(var(--foreground))",
                  fontWeight: "bold",
                  marginBottom: "0.25rem",
                }}
                itemStyle={{
                  color: "hsl(var(--primary))",
                }}
              />
              <Legend verticalAlign="top" height={36} />
              <Line
                type="monotone"
                dataKey="total"
                name="Excursões"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                activeDot={{ r: 6, strokeWidth: 0 }}
                dot={{ r: 4, strokeWidth: 0, fill: "hsl(var(--primary))" }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-muted-foreground text-center px-4">
              Nenhuma excursão encontrada para o período selecionado.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ExcursionsChart;
